/**
 * Code status — what is in force for a patient right now.
 *
 * This is the rule the emergency-UX prototype was built to validate, promoted
 * out of the prototype and rewritten against the real roster payload. The
 * prototype itself has since been removed.
 *
 * THE RULE: a patient is FULL CODE unless an NPI holder has validated a directive
 * record. Not "unless a record exists" — an unsigned record, however recently
 * submitted by however close a relative, does not change what a clinician should
 * do. `GetDirectivePatients` returns the most recent ACTIVE record's directives
 * and approver; the NPI check is applied here.
 *
 * COLOUR ENCODES COUNT, NOT SEVERITY. Both limited states read "CARE LIMITATIONS";
 * red means two or more limitations are in force, yellow means exactly one. A lone
 * "Do not resuscitate" therefore shows YELLOW while two milder limits show RED.
 * That is deliberate and was validated with clinical peers: the colour answers
 * "how much do I have to read before acting", not "how grave is this decision".
 * Do not "fix" it to track severity without taking that back to them.
 */

/** Palette per status key. Contrast-checked against white text where used. */
export const CODE_COLORS = {
  limits_many: { bg: "#b3261e", fg: "#ffffff" },
  limits_one: { bg: "#f2b705", fg: "#2b2000" },
  full: { bg: "#1b7f4b", fg: "#ffffff" },
};

/** The buckets, most-to-read first. One source of truth for filters and legend. */
export const CODE_BUCKETS = [
  { key: "limits_many", label: "Care limitations", note: "2 or more" },
  { key: "limits_one", label: "Care limitations", note: "1" },
  { key: "full", label: "Full Code", note: "none validated" },
];

const FULL_CODE_MEANING =
  "Attempt all resuscitative measures, including CPR and intubation.";

/** Why a patient is Full Code — shown small, never competing with the status. */
function fullCodeReason(row) {
  const pending = Number(row.pending_count) || 0;
  if (pending > 0) {
    return `${pending} record${pending === 1 ? "" : "s"} awaiting sign-off by an NPI holder.`;
  }
  if (Number(row.record_count) > 0) {
    return "No record has been validated by an NPI holder.";
  }
  return "No directive on file.";
}

/**
 * @param {object} row a row from GetDirectivePatients
 * @returns {{key,label,short,meaning,validated,directives,because,approver,npi,approvedAt}}
 */
export function codeStatus(row) {
  const npi = String(row.active_approved_by_npi || "").trim();
  const labels = String(row.active_directive_labels || "")
    .split("||")
    .map((s) => s.trim())
    .filter(Boolean);

  // An active record with no NPI on it is NOT validated — same as having none.
  if (!row.active_record_id || npi === "" || labels.length === 0) {
    return {
      key: "full",
      label: "FULL CODE",
      short: "Full Code",
      meaning: FULL_CODE_MEANING,
      validated: false,
      directives: [],
      because: fullCodeReason(row),
      approver: "",
      npi: "",
      approvedAt: "",
    };
  }

  const many = labels.length > 1;
  return {
    key: many ? "limits_many" : "limits_one",
    label: "CARE LIMITATIONS",
    short: "Care limitations",
    meaning: many
      ? `${labels.length} limitations are in force.`
      : "One limitation is in force.",
    validated: true,
    directives: labels,
    because: "",
    approver: row.active_approved_by_name || "",
    npi,
    approvedAt: row.active_approved_at ? String(row.active_approved_at).slice(0, 10) : "",
  };
}

/**
 * How the in-force directives read in a single line of a dense table.
 * A single directive is named outright — "Single directive: Do not resuscitate" —
 * because at a glance the count alone ("1 directive") tells a clinician nothing
 * they can act on, while the name does.
 */
export function inForceSummary(code) {
  if (!code.validated || !code.directives.length) return "";
  if (code.directives.length === 1) return `Single directive: ${code.directives[0]}`;
  return `${code.directives.length} directives`;
}
